import { array, bool, func, number, object, string } from 'prop-types';
import classNames from 'classnames';
import useIsMinimumMapHeight from '@alltrails/shared/hooks/useIsMinimumMapHeight';
import MapSelectorModal from '@alltrails/maps/components/MapSelectorModal';
import useStyleKeyboardShortcut from '@alltrails/maps/hooks/useStyleKeyboardShortcut';
import { getMapLocation } from '@alltrails/maps/utils/amplitude';
import { additionalStyleCardConfigs, baseStyleCardConfigs } from '@alltrails/maps/utils/cardConfigs';
import getStyleCardConfigs from '@alltrails/maps/utils/getStyleCardConfigs';
import IconButton from '@alltrails/shared/denali/components/IconButton';
import IconButtonGroup from '@alltrails/shared/denali/components/IconButtonGroup';
import Maps2d from '@alltrails/shared/icons/2d';
import Maps3d from '@alltrails/shared/icons/3d';
import Compass from '@alltrails/shared/icons/Compass';
import Layers from '@alltrails/shared/icons/Layers';
import useToggle from '@alltrails/shared/hooks/useToggle';
import { useIntl } from '@alltrails/shared/react-intl';
import LanguageSupportUtil from 'utils/language_support_util';
import LanguageRegionCodePropType from 'proptypes/LanguageRegionCodePropType';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import logThreeDMapButtonClicked from '@alltrails/analytics/events/logThreeDMapButtonClicked';
import logMapLayerButtonTapped from '@alltrails/analytics/events/logMapLayerButtonTapped';
import * as printStyles from './styles/print.module.scss';
import * as styles from './styles/styles.module.scss';

const MapSelection = ({
  onCardClick,
  onHeatmapClick,
  currentMapLayer,
  permissions,
  onActivityFilterChange,
  enabledOverlays,
  terrainActive,
  handleResetClick,
  selectedFilterActivity,
  adminCustomizationSettings,
  handleAdminCustomizationSettingsChange,
  mobileBrowser,
  adminLayout,
  currentLayerTerrainEnabled,
  terrainDisabled,
  handleDisable3D,
  handleEnable3D,
  languageRegionCode,
  isAuthenticated,
  isPro,
  handleCompassClick,
  compassRotation,
  trailId,
  isNotExploreMap,
  shouldConditionallyRender
}) => {
  const intl = useIntl();
  const isMinimumMapHeight = useIsMinimumMapHeight();
  const [open, toggle] = useToggle(false);

  let body;
  let hideForFullscreen;
  if (typeof window !== 'undefined') {
    body = document.querySelector('body');
    hideForFullscreen = document.querySelector('.hide-for-fullscreen-modal');
  }

  const closeModal = () => {
    toggle();
    if (body && hideForFullscreen) {
      body.style.overflow = 'unset';
      hideForFullscreen.style.display = '';
    }
  };

  const openModal = () => {
    logMapLayerButtonTapped({ map_location: getMapLocation(window.location.pathname) });
    toggle();
    if (body && hideForFullscreen) {
      body.style.overflow = 'hidden';
      hideForFullscreen.style.display = 'none';
    }
  };

  const getTerrainButton = () => {
    if (terrainDisabled || !currentLayerTerrainEnabled || !handleDisable3D || !handleEnable3D) {
      return null;
    }
    const onClick = () => {
      logThreeDMapButtonClicked({ map_location: getMapLocation(window.location.pathname), trail_id: trailId });

      if (terrainActive) {
        handleDisable3D();
      } else if (isPro) {
        handleEnable3D();
      } else {
        const params = new URLSearchParams();
        params.set('returnTo', window.location.toString());
        const path = `${isAuthenticated ? '/plus' : `/signup?${params.toString()}`}`;
        window.location = LanguageSupportUtil.wrapUrlSafe(path, languageRegionCode);
      }
    };
    const title = terrainActive ? intl.formatMessage({ defaultMessage: 'Disable 3D map' }) : intl.formatMessage({ defaultMessage: 'Enable 3D map' });
    return { icon: { Component: terrainActive ? Maps2d : Maps3d }, onClick, title, testId: title };
  };

  const { mapTypesCards, mapDetailsCards, additionalOptionsCards } = getStyleCardConfigs({
    currentMapLayer,
    enabledOverlays,
    isAdmin: adminLayout,
    additionalStyleCardConfigs,
    permissions,
    baseStyleCardConfigs,
    terrainActive,
    isUsingWebFeedPolylineColorExperiment: __AT_DATA__.experiments?.['web-feed-polyline-color']?.value === 'on'
  });

  // Note that this is defined outside the scope of the cards modal. These
  // shortcuts are meant to be used without having to open that modal
  useStyleKeyboardShortcut(styleId => {
    const config = [...mapDetailsCards, ...mapTypesCards].find(({ key }) => key === styleId);
    if (!config) {
      return;
    }

    onCardClick(config);
  });

  const shouldRenderButtons = !shouldConditionallyRender || isMinimumMapHeight;

  return (
    <div className={classNames(styles.controls, printStyles.controls, { [styles.isNotExploreMap]: isNotExploreMap })}>
      {shouldRenderButtons && (
        <>
          <IconButtonGroup
            className={terrainActive ? '' : styles.brandColor3DButton}
            buttons={[
              {
                icon: { Component: Layers },
                onClick: openModal,
                title: intl.formatMessage({ defaultMessage: 'Map options' }),
                testId: 'map-options'
              },
              getTerrainButton()
            ].filter(button => !!button)}
            orientation="vertical"
            testId="map-selection-controls"
          />
          {terrainDisabled ? null : (
            <IconButton
              className={styles.compass}
              icon={{ Component: Compass, orientation: compassRotation }}
              onClick={handleCompassClick}
              testId="map-compass"
              title={intl.formatMessage({ defaultMessage: 'Reset north and pitch' })}
              variant="elevated"
            />
          )}
        </>
      )}
      {open && (
        <MapSelectorModal
          mapTypesCards={mapTypesCards}
          mapDetailsCards={mapDetailsCards}
          additionalOptionsCards={additionalOptionsCards}
          handleClose={closeModal}
          handleResetClick={handleResetClick}
          handleCardClick={onCardClick}
          handleHeatmapClick={onHeatmapClick}
          handleActivityFilterChange={onActivityFilterChange}
          selectedFilterActivity={selectedFilterActivity}
          adminCustomizationSettings={adminCustomizationSettings}
          handleAdminCustomizationSettingsChange={handleAdminCustomizationSettingsChange}
          adminLayout={adminLayout}
          mobileBrowser={mobileBrowser}
          enabledOverlays={enabledOverlays}
        />
      )}
    </div>
  );
};

MapSelection.propTypes = {
  onCardClick: func.isRequired,
  onHeatmapClick: func.isRequired,
  currentMapLayer: string.isRequired,
  permissions: array.isRequired,
  onActivityFilterChange: func.isRequired,
  enabledOverlays: array.isRequired,
  adminCustomizationSettings: object,
  handleAdminCustomizationSettingsChange: func,
  terrainActive: bool,
  adminLayout: bool,
  mobileBrowser: bool,
  selectedFilterActivity: string,
  currentLayerTerrainEnabled: bool,
  terrainDisabled: bool,
  handleDisable3D: func,
  handleEnable3D: func,
  languageRegionCode: LanguageRegionCodePropType,
  isAuthenticated: bool,
  isPro: bool,
  handleCompassClick: func,
  compassRotation: number,
  trailId: number,
  shouldConditionallyRender: bool
};

MapSelection.defaultProps = {
  selectedFilterActivity: null,
  adminCustomizationSettings: {},
  adminLayout: false,
  mobileBrowser: false,
  terrainActive: false,
  handleAdminCustomizationSettingsChange: () => {},
  languageRegionCode: LanguageRegionCode.English,
  currentLayerTerrainEnabled: false,
  terrainDisabled: false,
  isAuthenticated: false,
  isPro: false,
  compassRotation: 0,
  trailId: -1,
  isNotExploreMap: true,
  shouldConditionallyRender: false
};

export default MapSelection;
